import { ITEMS_PER_PAGE } from "../../constants/constants";

import {
  // Dashboard
  FETCH_DASHBOARD,
  FETCH_UPCOMING_MAINTENANCE,
  FETCH_OVERDUE_MAINTENANCE,
  // Machine Types
  FETCH_MACHINE_TYPES,
  FETCH_MACHINE_TYPE,
  CREATE_MACHINE_TYPE,
  UPDATE_MACHINE_TYPE,
  DELETE_MACHINE_TYPE,
  // Facility Types
  FETCH_FACILITY_TYPES,
  FETCH_FACILITY_TYPE,
  CREATE_FACILITY_TYPE,
  UPDATE_FACILITY_TYPE,
  DELETE_FACILITY_TYPE,
  // Locations
  FETCH_MAINTENANCE_LOCATIONS,
  FETCH_MAINTENANCE_LOCATION,
  CREATE_MAINTENANCE_LOCATION,
  UPDATE_MAINTENANCE_LOCATION,
  DELETE_MAINTENANCE_LOCATION,
  // Service Providers
  FETCH_SERVICE_PROVIDERS,
  FETCH_SERVICE_PROVIDER,
  CREATE_SERVICE_PROVIDER,
  UPDATE_SERVICE_PROVIDER,
  DELETE_SERVICE_PROVIDER,
  // Machines
  FETCH_MACHINES,
  FETCH_MACHINE,
  CREATE_MACHINE,
  UPDATE_MACHINE,
  DELETE_MACHINE,
  START_MACHINE_MAINTENANCE,
  FETCH_MACHINE_MAINTENANCE_HISTORY,
  // Facilities
  FETCH_FACILITIES,
  FETCH_FACILITY,
  CREATE_FACILITY,
  UPDATE_FACILITY,
  DELETE_FACILITY,
  FETCH_FACILITY_TASKS,
  // Task Templates
  FETCH_TASK_TEMPLATES,
  FETCH_TASK_TEMPLATE,
  CREATE_TASK_TEMPLATE,
  UPDATE_TASK_TEMPLATE,
  DELETE_TASK_TEMPLATE,
  // Task Assignments
  FETCH_TASK_ASSIGNMENTS,
  FETCH_TASK_ASSIGNMENT,
  CREATE_TASK_ASSIGNMENT,
  UPDATE_TASK_ASSIGNMENT,
  DELETE_TASK_ASSIGNMENT,
  GENERATE_ASSIGNMENT_TASKS,
  GENERATE_ALL_TASKS,
  // Scheduled Tasks
  FETCH_SCHEDULED_TASKS,
  FETCH_SCHEDULED_TASK,
  FETCH_TASK_DASHBOARD,
  FETCH_TODAY_TASKS,
  FETCH_OVERDUE_TASKS,
  FETCH_UPCOMING_TASKS,
  START_SCHEDULED_TASK,
  COMPLETE_SCHEDULED_TASK,
  SKIP_SCHEDULED_TASK,
  ADD_TASK_PHOTO,
  // Maintenance Records
  FETCH_MAINTENANCE_RECORDS,
  FETCH_MAINTENANCE_RECORD,
  CREATE_MAINTENANCE_RECORD,
  UPDATE_MAINTENANCE_RECORD,
  DELETE_MAINTENANCE_RECORD,
  START_MAINTENANCE_RECORD,
  COMPLETE_MAINTENANCE_RECORD,
  CANCEL_MAINTENANCE_RECORD,
  RECORD_SERVICE_VISIT,
  // Photos
  FETCH_MAINTENANCE_PHOTOS,
  UPLOAD_MAINTENANCE_PHOTO,
  DELETE_MAINTENANCE_PHOTO,
  // Notifications
  FETCH_NOTIFICATIONS,
  FETCH_UNREAD_COUNT,
  MARK_NOTIFICATION_READ,
  MARK_ALL_NOTIFICATIONS_READ,
  // UI State
  CLEAR_MAINTENANCE_ERROR,
  SET_ACTIVE_TAB,
  CLEAR_CURRENT_MACHINE,
  CLEAR_CURRENT_RECORD,
  CLEAR_CURRENT_FACILITY,
  CLEAR_CURRENT_TASK,
  CLEAR_CURRENT_TASK_TEMPLATE,
  CLEAR_CURRENT_TASK_ASSIGNMENT,
} from "./types";

const INITIAL_STATE = {
  // Dashboard
  dashboard: {
    stats: null,
    upcoming: [],
    overdue: [],
    error: null,
    loading: false,
  },

  // Machine Types
  machineTypes: {
    list: [],
    current: null,
    hasMore: false,
    pages: 0,
    error: null,
    loading: false,
  },

  // Facility Types
  facilityTypes: {
    list: [],
    current: null,
    hasMore: false,
    pages: 0,
    error: null,
    loading: false,
  },

  // Locations
  locations: {
    list: [],
    current: null,
    hasMore: false,
    pages: 0,
    error: null,
    loading: false,
  },

  // Service Providers
  serviceProviders: {
    list: [],
    current: null,
    hasMore: false,
    pages: 0,
    error: null,
    loading: false,
  },

  // Machines
  machines: {
    list: [],
    current: null,
    maintenanceHistory: [],
    hasMore: false,
    pages: 0,
    error: null,
    loading: false,
  },

  // Facilities
  facilities: {
    list: [],
    current: null,
    tasks: [],
    hasMore: false,
    pages: 0,
    error: null,
    loading: false,
  },

  // Task Templates
  taskTemplates: {
    list: [],
    current: null,
    hasMore: false,
    pages: 0,
    page: 1,
    error: null,
    loading: false,
  },

  // Task Assignments
  taskAssignments: {
    list: [],
    current: null,
    hasMore: false,
    pages: 0,
    page: 1,
    error: null,
    loading: false,
  },

  // Scheduled Tasks
  scheduledTasks: {
    list: [],
    current: null,
    today: [],
    overdue: [],
    upcoming: [],
    dashboardStats: null,
    error: null,
    loading: false,
  },

  // Maintenance Records
  records: {
    list: [],
    current: null,
    hasMore: false,
    pages: 0,
    error: null,
    loading: false,
  },

  // Photos
  photos: {
    list: [],
    hasMore: false,
    pages: 0,
    error: null,
    loading: false,
  },

  // Notifications
  notifications: {
    list: [],
    hasMore: false,
    pages: 0,
    unreadCount: 0,
    error: null,
    loading: false,
  },

  // UI State
  ui: {
    activeTab: "dashboard",
    error: null,
  },
};

// Helper to extract list from paginated response
const extractList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (payload && payload.results) return payload.results;
  return [];
};

const maintenanceReducer = (state = INITIAL_STATE, action) => {
  switch (action.type) {
    // =========================================================================
    // Dashboard
    // =========================================================================
    case FETCH_DASHBOARD.init:
      return {
        ...state,
        dashboard: { ...state.dashboard, error: null, loading: true },
      };
    case FETCH_DASHBOARD.success:
      return {
        ...state,
        dashboard: {
          ...state.dashboard,
          stats: action.payload,
          error: null,
          loading: false,
        },
      };
    case FETCH_DASHBOARD.failure:
      return {
        ...state,
        dashboard: { ...state.dashboard, error: action.payload, loading: false },
      };

    case FETCH_UPCOMING_MAINTENANCE.init:
      return {
        ...state,
        dashboard: { ...state.dashboard, loading: true },
      };
    case FETCH_UPCOMING_MAINTENANCE.success:
      return {
        ...state,
        dashboard: {
          ...state.dashboard,
          upcoming: extractList(action.payload),
          loading: false,
        },
      };
    case FETCH_UPCOMING_MAINTENANCE.failure:
      return {
        ...state,
        dashboard: { ...state.dashboard, error: action.payload, loading: false },
      };

    case FETCH_OVERDUE_MAINTENANCE.init:
      return {
        ...state,
        dashboard: { ...state.dashboard, loading: true },
      };
    case FETCH_OVERDUE_MAINTENANCE.success:
      return {
        ...state,
        dashboard: {
          ...state.dashboard,
          overdue: extractList(action.payload),
          loading: false,
        },
      };
    case FETCH_OVERDUE_MAINTENANCE.failure:
      return {
        ...state,
        dashboard: { ...state.dashboard, error: action.payload, loading: false },
      };

    // =========================================================================
    // Machine Types
    // =========================================================================
    case FETCH_MACHINE_TYPES.init:
      return {
        ...state,
        machineTypes: { ...state.machineTypes, hasMore: false, error: null, loading: true },
      };
    case FETCH_MACHINE_TYPES.success:
      return {
        ...state,
        machineTypes: {
          ...state.machineTypes,
          list: action.payload.results || [],
          hasMore: action.payload.next !== null,
          pages: Math.ceil((action.payload.count || 0) / ITEMS_PER_PAGE),
          error: null,
          loading: false,
        },
      };
    case FETCH_MACHINE_TYPES.failure:
      return {
        ...state,
        machineTypes: { ...state.machineTypes, error: action.payload, loading: false },
      };

    case FETCH_MACHINE_TYPE.init:
      return {
        ...state,
        machineTypes: { ...state.machineTypes, error: null, loading: true },
      };
    case FETCH_MACHINE_TYPE.success:
      return {
        ...state,
        machineTypes: {
          ...state.machineTypes,
          current: action.payload,
          error: null,
          loading: false,
        },
      };
    case FETCH_MACHINE_TYPE.failure:
      return {
        ...state,
        machineTypes: { ...state.machineTypes, error: action.payload, loading: false },
      };

    case CREATE_MACHINE_TYPE.init:
      return {
        ...state,
        machineTypes: { ...state.machineTypes, error: null, loading: true },
      };
    case CREATE_MACHINE_TYPE.success:
      return {
        ...state,
        machineTypes: {
          ...state.machineTypes,
          list: [...state.machineTypes.list, action.payload],
          error: null,
          loading: false,
        },
      };
    case CREATE_MACHINE_TYPE.failure:
      return {
        ...state,
        machineTypes: { ...state.machineTypes, error: action.payload, loading: false },
      };

    case UPDATE_MACHINE_TYPE.init:
      return {
        ...state,
        machineTypes: { ...state.machineTypes, error: null, loading: true },
      };
    case UPDATE_MACHINE_TYPE.success:
      return {
        ...state,
        machineTypes: {
          ...state.machineTypes,
          list: state.machineTypes.list.map((item) =>
            item.id === action.payload.id ? action.payload : item
          ),
          current: action.payload,
          error: null,
          loading: false,
        },
      };
    case UPDATE_MACHINE_TYPE.failure:
      return {
        ...state,
        machineTypes: { ...state.machineTypes, error: action.payload, loading: false },
      };

    case DELETE_MACHINE_TYPE.init:
      return {
        ...state,
        machineTypes: { ...state.machineTypes, error: null, loading: true },
      };
    case DELETE_MACHINE_TYPE.success:
      return {
        ...state,
        machineTypes: {
          ...state.machineTypes,
          list: state.machineTypes.list.filter((item) => item.id !== action.payload),
          error: null,
          loading: false,
        },
      };
    case DELETE_MACHINE_TYPE.failure:
      return {
        ...state,
        machineTypes: { ...state.machineTypes, error: action.payload, loading: false },
      };

    // =========================================================================
    // Facility Types
    // =========================================================================
    case FETCH_FACILITY_TYPES.init:
      return {
        ...state,
        facilityTypes: { ...state.facilityTypes, hasMore: false, error: null, loading: true },
      };
    case FETCH_FACILITY_TYPES.success:
      return {
        ...state,
        facilityTypes: {
          ...state.facilityTypes,
          list: action.payload.results || [],
          hasMore: action.payload.next !== null,
          pages: Math.ceil((action.payload.count || 0) / ITEMS_PER_PAGE),
          error: null,
          loading: false,
        },
      };
    case FETCH_FACILITY_TYPES.failure:
      return {
        ...state,
        facilityTypes: { ...state.facilityTypes, error: action.payload, loading: false },
      };

    case FETCH_FACILITY_TYPE.init:
      return {
        ...state,
        facilityTypes: { ...state.facilityTypes, error: null, loading: true },
      };
    case FETCH_FACILITY_TYPE.success:
      return {
        ...state,
        facilityTypes: {
          ...state.facilityTypes,
          current: action.payload,
          error: null,
          loading: false,
        },
      };
    case FETCH_FACILITY_TYPE.failure:
      return {
        ...state,
        facilityTypes: { ...state.facilityTypes, error: action.payload, loading: false },
      };

    case CREATE_FACILITY_TYPE.init:
      return {
        ...state,
        facilityTypes: { ...state.facilityTypes, error: null, loading: true },
      };
    case CREATE_FACILITY_TYPE.success:
      return {
        ...state,
        facilityTypes: {
          ...state.facilityTypes,
          list: [...state.facilityTypes.list, action.payload],
          error: null,
          loading: false,
        },
      };
    case CREATE_FACILITY_TYPE.failure:
      return {
        ...state,
        facilityTypes: { ...state.facilityTypes, error: action.payload, loading: false },
      };

    case UPDATE_FACILITY_TYPE.init:
      return {
        ...state,
        facilityTypes: { ...state.facilityTypes, error: null, loading: true },
      };
    case UPDATE_FACILITY_TYPE.success:
      return {
        ...state,
        facilityTypes: {
          ...state.facilityTypes,
          list: state.facilityTypes.list.map((item) =>
            item.id === action.payload.id ? action.payload : item
          ),
          current: action.payload,
          error: null,
          loading: false,
        },
      };
    case UPDATE_FACILITY_TYPE.failure:
      return {
        ...state,
        facilityTypes: { ...state.facilityTypes, error: action.payload, loading: false },
      };

    case DELETE_FACILITY_TYPE.init:
      return {
        ...state,
        facilityTypes: { ...state.facilityTypes, error: null, loading: true },
      };
    case DELETE_FACILITY_TYPE.success:
      return {
        ...state,
        facilityTypes: {
          ...state.facilityTypes,
          list: state.facilityTypes.list.filter((item) => item.id !== action.payload),
          error: null,
          loading: false,
        },
      };
    case DELETE_FACILITY_TYPE.failure:
      return {
        ...state,
        facilityTypes: { ...state.facilityTypes, error: action.payload, loading: false },
      };

    // =========================================================================
    // Locations
    // =========================================================================
    case FETCH_MAINTENANCE_LOCATIONS.init:
      return {
        ...state,
        locations: { ...state.locations, hasMore: false, error: null, loading: true },
      };
    case FETCH_MAINTENANCE_LOCATIONS.success:
      return {
        ...state,
        locations: {
          ...state.locations,
          list: action.payload.results || [],
          hasMore: action.payload.next !== null,
          pages: Math.ceil((action.payload.count || 0) / ITEMS_PER_PAGE),
          error: null,
          loading: false,
        },
      };
    case FETCH_MAINTENANCE_LOCATIONS.failure:
      return {
        ...state,
        locations: { ...state.locations, error: action.payload, loading: false },
      };

    case FETCH_MAINTENANCE_LOCATION.init:
      return {
        ...state,
        locations: { ...state.locations, error: null, loading: true },
      };
    case FETCH_MAINTENANCE_LOCATION.success:
      return {
        ...state,
        locations: {
          ...state.locations,
          current: action.payload,
          error: null,
          loading: false,
        },
      };
    case FETCH_MAINTENANCE_LOCATION.failure:
      return {
        ...state,
        locations: { ...state.locations, error: action.payload, loading: false },
      };

    case CREATE_MAINTENANCE_LOCATION.init:
      return {
        ...state,
        locations: { ...state.locations, error: null, loading: true },
      };
    case CREATE_MAINTENANCE_LOCATION.success:
      return {
        ...state,
        locations: {
          ...state.locations,
          list: [...state.locations.list, action.payload],
          error: null,
          loading: false,
        },
      };
    case CREATE_MAINTENANCE_LOCATION.failure:
      return {
        ...state,
        locations: { ...state.locations, error: action.payload, loading: false },
      };

    case UPDATE_MAINTENANCE_LOCATION.init:
      return {
        ...state,
        locations: { ...state.locations, error: null, loading: true },
      };
    case UPDATE_MAINTENANCE_LOCATION.success:
      return {
        ...state,
        locations: {
          ...state.locations,
          list: state.locations.list.map((item) =>
            item.id === action.payload.id ? action.payload : item
          ),
          current: action.payload,
          error: null,
          loading: false,
        },
      };
    case UPDATE_MAINTENANCE_LOCATION.failure:
      return {
        ...state,
        locations: { ...state.locations, error: action.payload, loading: false },
      };

    case DELETE_MAINTENANCE_LOCATION.init:
      return {
        ...state,
        locations: { ...state.locations, error: null, loading: true },
      };
    case DELETE_MAINTENANCE_LOCATION.success:
      return {
        ...state,
        locations: {
          ...state.locations,
          list: state.locations.list.filter((item) => item.id !== action.payload),
          error: null,
          loading: false,
        },
      };
    case DELETE_MAINTENANCE_LOCATION.failure:
      return {
        ...state,
        locations: { ...state.locations, error: action.payload, loading: false },
      };

    // =========================================================================
    // Service Providers
    // =========================================================================
    case FETCH_SERVICE_PROVIDERS.init:
      return {
        ...state,
        serviceProviders: { ...state.serviceProviders, hasMore: false, error: null, loading: true },
      };
    case FETCH_SERVICE_PROVIDERS.success:
      return {
        ...state,
        serviceProviders: {
          ...state.serviceProviders,
          list: action.payload.results || [],
          hasMore: action.payload.next !== null,
          pages: Math.ceil((action.payload.count || 0) / ITEMS_PER_PAGE),
          error: null,
          loading: false,
        },
      };
    case FETCH_SERVICE_PROVIDERS.failure:
      return {
        ...state,
        serviceProviders: { ...state.serviceProviders, error: action.payload, loading: false },
      };

    case FETCH_SERVICE_PROVIDER.init:
      return {
        ...state,
        serviceProviders: { ...state.serviceProviders, error: null, loading: true },
      };
    case FETCH_SERVICE_PROVIDER.success:
      return {
        ...state,
        serviceProviders: {
          ...state.serviceProviders,
          current: action.payload,
          error: null,
          loading: false,
        },
      };
    case FETCH_SERVICE_PROVIDER.failure:
      return {
        ...state,
        serviceProviders: { ...state.serviceProviders, error: action.payload, loading: false },
      };

    case CREATE_SERVICE_PROVIDER.init:
      return {
        ...state,
        serviceProviders: { ...state.serviceProviders, error: null, loading: true },
      };
    case CREATE_SERVICE_PROVIDER.success:
      return {
        ...state,
        serviceProviders: {
          ...state.serviceProviders,
          list: [...state.serviceProviders.list, action.payload],
          error: null,
          loading: false,
        },
      };
    case CREATE_SERVICE_PROVIDER.failure:
      return {
        ...state,
        serviceProviders: { ...state.serviceProviders, error: action.payload, loading: false },
      };

    case UPDATE_SERVICE_PROVIDER.init:
      return {
        ...state,
        serviceProviders: { ...state.serviceProviders, error: null, loading: true },
      };
    case UPDATE_SERVICE_PROVIDER.success:
      return {
        ...state,
        serviceProviders: {
          ...state.serviceProviders,
          list: state.serviceProviders.list.map((item) =>
            item.id === action.payload.id ? action.payload : item
          ),
          current: action.payload,
          error: null,
          loading: false,
        },
      };
    case UPDATE_SERVICE_PROVIDER.failure:
      return {
        ...state,
        serviceProviders: { ...state.serviceProviders, error: action.payload, loading: false },
      };

    case DELETE_SERVICE_PROVIDER.init:
      return {
        ...state,
        serviceProviders: { ...state.serviceProviders, error: null, loading: true },
      };
    case DELETE_SERVICE_PROVIDER.success:
      return {
        ...state,
        serviceProviders: {
          ...state.serviceProviders,
          list: state.serviceProviders.list.filter((item) => item.id !== action.payload),
          error: null,
          loading: false,
        },
      };
    case DELETE_SERVICE_PROVIDER.failure:
      return {
        ...state,
        serviceProviders: { ...state.serviceProviders, error: action.payload, loading: false },
      };

    // =========================================================================
    // Machines
    // =========================================================================
    case FETCH_MACHINES.init:
      return {
        ...state,
        machines: { ...state.machines, hasMore: false, error: null, loading: true },
      };
    case FETCH_MACHINES.success:
      return {
        ...state,
        machines: {
          ...state.machines,
          list: action.payload.results,
          hasMore: action.payload.next !== null,
          pages: Math.ceil(action.payload.count / ITEMS_PER_PAGE),
          error: null,
          loading: false,
        },
      };
    case FETCH_MACHINES.failure:
      return {
        ...state,
        machines: { ...state.machines, error: action.payload, loading: false },
      };

    case FETCH_MACHINE.init:
      return {
        ...state,
        machines: { ...state.machines, error: null, loading: true },
      };
    case FETCH_MACHINE.success:
      return {
        ...state,
        machines: {
          ...state.machines,
          current: action.payload,
          error: null,
          loading: false,
        },
      };
    case FETCH_MACHINE.failure:
      return {
        ...state,
        machines: { ...state.machines, error: action.payload, loading: false },
      };

    case CREATE_MACHINE.init:
      return {
        ...state,
        machines: { ...state.machines, error: null, loading: true },
      };
    case CREATE_MACHINE.success:
      return {
        ...state,
        machines: {
          ...state.machines,
          list: [...state.machines.list, action.payload],
          error: null,
          loading: false,
        },
      };
    case CREATE_MACHINE.failure:
      return {
        ...state,
        machines: { ...state.machines, error: action.payload, loading: false },
      };

    case UPDATE_MACHINE.init:
      return {
        ...state,
        machines: { ...state.machines, error: null, loading: true },
      };
    case UPDATE_MACHINE.success:
      return {
        ...state,
        machines: {
          ...state.machines,
          list: state.machines.list.map((item) =>
            item.id === action.payload.id ? action.payload : item
          ),
          current: action.payload,
          error: null,
          loading: false,
        },
      };
    case UPDATE_MACHINE.failure:
      return {
        ...state,
        machines: { ...state.machines, error: action.payload, loading: false },
      };

    case DELETE_MACHINE.init:
      return {
        ...state,
        machines: { ...state.machines, error: null, loading: true },
      };
    case DELETE_MACHINE.success:
      return {
        ...state,
        machines: {
          ...state.machines,
          list: state.machines.list.filter((item) => item.id !== action.payload),
          error: null,
          loading: false,
        },
      };
    case DELETE_MACHINE.failure:
      return {
        ...state,
        machines: { ...state.machines, error: action.payload, loading: false },
      };

    case START_MACHINE_MAINTENANCE.init:
      return {
        ...state,
        machines: { ...state.machines, loading: true },
      };
    case START_MACHINE_MAINTENANCE.success:
      return {
        ...state,
        machines: {
          ...state.machines,
          loading: false,
        },
      };
    case START_MACHINE_MAINTENANCE.failure:
      return {
        ...state,
        machines: { ...state.machines, error: action.payload, loading: false },
      };

    case FETCH_MACHINE_MAINTENANCE_HISTORY.init:
      return {
        ...state,
        machines: { ...state.machines, loading: true },
      };
    case FETCH_MACHINE_MAINTENANCE_HISTORY.success:
      return {
        ...state,
        machines: {
          ...state.machines,
          maintenanceHistory: extractList(action.payload),
          loading: false,
        },
      };
    case FETCH_MACHINE_MAINTENANCE_HISTORY.failure:
      return {
        ...state,
        machines: { ...state.machines, error: action.payload, loading: false },
      };

    case CLEAR_CURRENT_MACHINE:
      return {
        ...state,
        machines: { ...state.machines, current: null, maintenanceHistory: [] },
      };

    // =========================================================================
    // Facilities
    // =========================================================================
    case FETCH_FACILITIES.init:
      return {
        ...state,
        facilities: { ...state.facilities, hasMore: false, error: null, loading: true },
      };
    case FETCH_FACILITIES.success:
      return {
        ...state,
        facilities: {
          ...state.facilities,
          list: action.payload.results,
          hasMore: action.payload.next !== null,
          pages: Math.ceil(action.payload.count / ITEMS_PER_PAGE),
          error: null,
          loading: false,
        },
      };
    case FETCH_FACILITIES.failure:
      return {
        ...state,
        facilities: { ...state.facilities, error: action.payload, loading: false },
      };

    case FETCH_FACILITY.init:
      return {
        ...state,
        facilities: { ...state.facilities, error: null, loading: true },
      };
    case FETCH_FACILITY.success:
      return {
        ...state,
        facilities: {
          ...state.facilities,
          current: action.payload,
          error: null,
          loading: false,
        },
      };
    case FETCH_FACILITY.failure:
      return {
        ...state,
        facilities: { ...state.facilities, error: action.payload, loading: false },
      };

    case CREATE_FACILITY.init:
      return {
        ...state,
        facilities: { ...state.facilities, error: null, loading: true },
      };
    case CREATE_FACILITY.success:
      return {
        ...state,
        facilities: {
          ...state.facilities,
          list: [...state.facilities.list, action.payload],
          error: null,
          loading: false,
        },
      };
    case CREATE_FACILITY.failure:
      return {
        ...state,
        facilities: { ...state.facilities, error: action.payload, loading: false },
      };

    case UPDATE_FACILITY.init:
      return {
        ...state,
        facilities: { ...state.facilities, error: null, loading: true },
      };
    case UPDATE_FACILITY.success:
      return {
        ...state,
        facilities: {
          ...state.facilities,
          list: state.facilities.list.map((item) =>
            item.id === action.payload.id ? action.payload : item
          ),
          current: action.payload,
          error: null,
          loading: false,
        },
      };
    case UPDATE_FACILITY.failure:
      return {
        ...state,
        facilities: { ...state.facilities, error: action.payload, loading: false },
      };

    case DELETE_FACILITY.init:
      return {
        ...state,
        facilities: { ...state.facilities, error: null, loading: true },
      };
    case DELETE_FACILITY.success:
      return {
        ...state,
        facilities: {
          ...state.facilities,
          list: state.facilities.list.filter((item) => item.id !== action.payload),
          error: null,
          loading: false,
        },
      };
    case DELETE_FACILITY.failure:
      return {
        ...state,
        facilities: { ...state.facilities, error: action.payload, loading: false },
      };

    case FETCH_FACILITY_TASKS.init:
      return {
        ...state,
        facilities: { ...state.facilities, loading: true },
      };
    case FETCH_FACILITY_TASKS.success:
      return {
        ...state,
        facilities: {
          ...state.facilities,
          tasks: extractList(action.payload),
          loading: false,
        },
      };
    case FETCH_FACILITY_TASKS.failure:
      return {
        ...state,
        facilities: { ...state.facilities, error: action.payload, loading: false },
      };

    case CLEAR_CURRENT_FACILITY:
      return {
        ...state,
        facilities: { ...state.facilities, current: null, tasks: [] },
      };

    // =========================================================================
    // Task Templates
    // =========================================================================
    case FETCH_TASK_TEMPLATES.init:
      return {
        ...state,
        taskTemplates: { ...state.taskTemplates, hasMore: false, error: null, loading: true },
      };
    case FETCH_TASK_TEMPLATES.success:
      return {
        ...state,
        taskTemplates: {
          ...state.taskTemplates,
          list: action.payload.results || [],
          hasMore: action.payload.next !== null,
          pages: Math.ceil((action.payload.count || 0) / ITEMS_PER_PAGE),
          error: null,
          loading: false,
        },
      };
    case FETCH_TASK_TEMPLATES.failure:
      return {
        ...state,
        taskTemplates: { ...state.taskTemplates, error: action.payload, loading: false },
      };

    case FETCH_TASK_TEMPLATE.init:
      return {
        ...state,
        taskTemplates: { ...state.taskTemplates, error: null, loading: true },
      };
    case FETCH_TASK_TEMPLATE.success:
      return {
        ...state,
        taskTemplates: {
          ...state.taskTemplates,
          current: action.payload,
          error: null,
          loading: false,
        },
      };
    case FETCH_TASK_TEMPLATE.failure:
      return {
        ...state,
        taskTemplates: { ...state.taskTemplates, error: action.payload, loading: false },
      };

    case CREATE_TASK_TEMPLATE.init:
      return {
        ...state,
        taskTemplates: { ...state.taskTemplates, error: null, loading: true },
      };
    case CREATE_TASK_TEMPLATE.success:
      return {
        ...state,
        taskTemplates: {
          ...state.taskTemplates,
          list: [...state.taskTemplates.list, action.payload],
          error: null,
          loading: false,
        },
      };
    case CREATE_TASK_TEMPLATE.failure:
      return {
        ...state,
        taskTemplates: { ...state.taskTemplates, error: action.payload, loading: false },
      };

    case UPDATE_TASK_TEMPLATE.init:
      return {
        ...state,
        taskTemplates: { ...state.taskTemplates, error: null, loading: true },
      };
    case UPDATE_TASK_TEMPLATE.success:
      return {
        ...state,
        taskTemplates: {
          ...state.taskTemplates,
          list: state.taskTemplates.list.map((item) =>
            item.id === action.payload.id ? action.payload : item
          ),
          current: action.payload,
          error: null,
          loading: false,
        },
      };
    case UPDATE_TASK_TEMPLATE.failure:
      return {
        ...state,
        taskTemplates: { ...state.taskTemplates, error: action.payload, loading: false },
      };

    case DELETE_TASK_TEMPLATE.init:
      return {
        ...state,
        taskTemplates: { ...state.taskTemplates, error: null, loading: true },
      };
    case DELETE_TASK_TEMPLATE.success:
      return {
        ...state,
        taskTemplates: {
          ...state.taskTemplates,
          list: state.taskTemplates.list.filter((item) => item.id !== action.payload),
          error: null,
          loading: false,
        },
      };
    case DELETE_TASK_TEMPLATE.failure:
      return {
        ...state,
        taskTemplates: { ...state.taskTemplates, error: action.payload, loading: false },
      };

    // =========================================================================
    // Task Assignments
    // =========================================================================
    case FETCH_TASK_ASSIGNMENTS.init:
      return {
        ...state,
        taskAssignments: { ...state.taskAssignments, hasMore: false, error: null, loading: true },
      };
    case FETCH_TASK_ASSIGNMENTS.success:
      return {
        ...state,
        taskAssignments: {
          ...state.taskAssignments,
          list: action.payload.results || [],
          hasMore: action.payload.next !== null,
          pages: Math.ceil((action.payload.count || 0) / ITEMS_PER_PAGE),
          error: null,
          loading: false,
        },
      };
    case FETCH_TASK_ASSIGNMENTS.failure:
      return {
        ...state,
        taskAssignments: { ...state.taskAssignments, error: action.payload, loading: false },
      };

    case FETCH_TASK_ASSIGNMENT.init:
      return {
        ...state,
        taskAssignments: { ...state.taskAssignments, error: null, loading: true },
      };
    case FETCH_TASK_ASSIGNMENT.success:
      return {
        ...state,
        taskAssignments: {
          ...state.taskAssignments,
          current: action.payload,
          error: null,
          loading: false,
        },
      };
    case FETCH_TASK_ASSIGNMENT.failure:
      return {
        ...state,
        taskAssignments: { ...state.taskAssignments, error: action.payload, loading: false },
      };

    case CREATE_TASK_ASSIGNMENT.init:
      return {
        ...state,
        taskAssignments: { ...state.taskAssignments, error: null, loading: true },
      };
    case CREATE_TASK_ASSIGNMENT.success:
      return {
        ...state,
        taskAssignments: {
          ...state.taskAssignments,
          list: [...state.taskAssignments.list, action.payload],
          error: null,
          loading: false,
        },
      };
    case CREATE_TASK_ASSIGNMENT.failure:
      return {
        ...state,
        taskAssignments: { ...state.taskAssignments, error: action.payload, loading: false },
      };

    case UPDATE_TASK_ASSIGNMENT.init:
      return {
        ...state,
        taskAssignments: { ...state.taskAssignments, error: null, loading: true },
      };
    case UPDATE_TASK_ASSIGNMENT.success:
      return {
        ...state,
        taskAssignments: {
          ...state.taskAssignments,
          list: state.taskAssignments.list.map((item) =>
            item.id === action.payload.id ? action.payload : item
          ),
          current: action.payload,
          error: null,
          loading: false,
        },
      };
    case UPDATE_TASK_ASSIGNMENT.failure:
      return {
        ...state,
        taskAssignments: { ...state.taskAssignments, error: action.payload, loading: false },
      };

    case DELETE_TASK_ASSIGNMENT.init:
      return {
        ...state,
        taskAssignments: { ...state.taskAssignments, error: null, loading: true },
      };
    case DELETE_TASK_ASSIGNMENT.success:
      return {
        ...state,
        taskAssignments: {
          ...state.taskAssignments,
          list: state.taskAssignments.list.filter((item) => item.id !== action.payload),
          error: null,
          loading: false,
        },
      };
    case DELETE_TASK_ASSIGNMENT.failure:
      return {
        ...state,
        taskAssignments: { ...state.taskAssignments, error: action.payload, loading: false },
      };

    case GENERATE_ASSIGNMENT_TASKS.init:
    case GENERATE_ALL_TASKS.init:
      return {
        ...state,
        taskAssignments: { ...state.taskAssignments, loading: true },
      };
    case GENERATE_ASSIGNMENT_TASKS.success:
    case GENERATE_ALL_TASKS.success:
      return {
        ...state,
        taskAssignments: { ...state.taskAssignments, loading: false },
      };
    case GENERATE_ASSIGNMENT_TASKS.failure:
    case GENERATE_ALL_TASKS.failure:
      return {
        ...state,
        taskAssignments: { ...state.taskAssignments, error: action.payload, loading: false },
      };

    // =========================================================================
    // Scheduled Tasks
    // =========================================================================
    case FETCH_SCHEDULED_TASKS.init:
      return {
        ...state,
        scheduledTasks: { ...state.scheduledTasks, error: null, loading: true },
      };
    case FETCH_SCHEDULED_TASKS.success:
      return {
        ...state,
        scheduledTasks: {
          ...state.scheduledTasks,
          list: extractList(action.payload),
          error: null,
          loading: false,
        },
      };
    case FETCH_SCHEDULED_TASKS.failure:
      return {
        ...state,
        scheduledTasks: { ...state.scheduledTasks, error: action.payload, loading: false },
      };

    case FETCH_SCHEDULED_TASK.init:
      return {
        ...state,
        scheduledTasks: { ...state.scheduledTasks, error: null, loading: true },
      };
    case FETCH_SCHEDULED_TASK.success:
      return {
        ...state,
        scheduledTasks: {
          ...state.scheduledTasks,
          current: action.payload,
          error: null,
          loading: false,
        },
      };
    case FETCH_SCHEDULED_TASK.failure:
      return {
        ...state,
        scheduledTasks: { ...state.scheduledTasks, error: action.payload, loading: false },
      };

    case FETCH_TASK_DASHBOARD.init:
      return {
        ...state,
        scheduledTasks: { ...state.scheduledTasks, loading: true },
      };
    case FETCH_TASK_DASHBOARD.success:
      return {
        ...state,
        scheduledTasks: {
          ...state.scheduledTasks,
          dashboardStats: action.payload,
          loading: false,
        },
      };
    case FETCH_TASK_DASHBOARD.failure:
      return {
        ...state,
        scheduledTasks: { ...state.scheduledTasks, error: action.payload, loading: false },
      };

    case FETCH_TODAY_TASKS.init:
      return {
        ...state,
        scheduledTasks: { ...state.scheduledTasks, loading: true },
      };
    case FETCH_TODAY_TASKS.success:
      return {
        ...state,
        scheduledTasks: {
          ...state.scheduledTasks,
          today: extractList(action.payload),
          loading: false,
        },
      };
    case FETCH_TODAY_TASKS.failure:
      return {
        ...state,
        scheduledTasks: { ...state.scheduledTasks, error: action.payload, loading: false },
      };

    case FETCH_OVERDUE_TASKS.init:
      return {
        ...state,
        scheduledTasks: { ...state.scheduledTasks, loading: true },
      };
    case FETCH_OVERDUE_TASKS.success:
      return {
        ...state,
        scheduledTasks: {
          ...state.scheduledTasks,
          overdue: extractList(action.payload),
          loading: false,
        },
      };
    case FETCH_OVERDUE_TASKS.failure:
      return {
        ...state,
        scheduledTasks: { ...state.scheduledTasks, error: action.payload, loading: false },
      };

    case FETCH_UPCOMING_TASKS.init:
      return {
        ...state,
        scheduledTasks: { ...state.scheduledTasks, loading: true },
      };
    case FETCH_UPCOMING_TASKS.success:
      return {
        ...state,
        scheduledTasks: {
          ...state.scheduledTasks,
          upcoming: extractList(action.payload),
          loading: false,
        },
      };
    case FETCH_UPCOMING_TASKS.failure:
      return {
        ...state,
        scheduledTasks: { ...state.scheduledTasks, error: action.payload, loading: false },
      };

    case START_SCHEDULED_TASK.init:
    case COMPLETE_SCHEDULED_TASK.init:
    case SKIP_SCHEDULED_TASK.init:
      return {
        ...state,
        scheduledTasks: { ...state.scheduledTasks, loading: true },
      };
    case START_SCHEDULED_TASK.success:
    case COMPLETE_SCHEDULED_TASK.success:
    case SKIP_SCHEDULED_TASK.success:
      const updatedTask = action.payload.data || action.payload;
      return {
        ...state,
        scheduledTasks: {
          ...state.scheduledTasks,
          list: state.scheduledTasks.list.map((item) =>
            item.id === updatedTask.id ? { ...item, ...updatedTask } : item
          ),
          today: state.scheduledTasks.today.map((item) =>
            item.id === updatedTask.id ? { ...item, ...updatedTask } : item
          ),
          overdue: state.scheduledTasks.overdue.filter((item) => item.id !== updatedTask.id),
          current: state.scheduledTasks.current?.id === updatedTask.id
            ? { ...state.scheduledTasks.current, ...updatedTask }
            : state.scheduledTasks.current,
          loading: false,
        },
      };
    case START_SCHEDULED_TASK.failure:
    case COMPLETE_SCHEDULED_TASK.failure:
    case SKIP_SCHEDULED_TASK.failure:
      return {
        ...state,
        scheduledTasks: { ...state.scheduledTasks, error: action.payload, loading: false },
      };

    case ADD_TASK_PHOTO.init:
      return {
        ...state,
        scheduledTasks: { ...state.scheduledTasks, loading: true },
      };
    case ADD_TASK_PHOTO.success:
      const photoData = action.payload.data || action.payload;
      return {
        ...state,
        scheduledTasks: {
          ...state.scheduledTasks,
          current: state.scheduledTasks.current
            ? {
                ...state.scheduledTasks.current,
                photos: [...(state.scheduledTasks.current.photos || []), photoData],
              }
            : null,
          loading: false,
        },
      };
    case ADD_TASK_PHOTO.failure:
      return {
        ...state,
        scheduledTasks: { ...state.scheduledTasks, error: action.payload, loading: false },
      };

    case CLEAR_CURRENT_TASK:
      return {
        ...state,
        scheduledTasks: { ...state.scheduledTasks, current: null },
      };

    case CLEAR_CURRENT_TASK_TEMPLATE:
      return {
        ...state,
        taskTemplates: { ...state.taskTemplates, current: null },
      };

    case CLEAR_CURRENT_TASK_ASSIGNMENT:
      return {
        ...state,
        taskAssignments: { ...state.taskAssignments, current: null },
      };

    // =========================================================================
    // Maintenance Records
    // =========================================================================
    case FETCH_MAINTENANCE_RECORDS.init:
      return {
        ...state,
        records: { ...state.records, hasMore: false, error: null, loading: true },
      };
    case FETCH_MAINTENANCE_RECORDS.success:
      return {
        ...state,
        records: {
          ...state.records,
          list: action.payload.results || [],
          hasMore: action.payload.next !== null,
          pages: Math.ceil((action.payload.count || 0) / ITEMS_PER_PAGE),
          error: null,
          loading: false,
        },
      };
    case FETCH_MAINTENANCE_RECORDS.failure:
      return {
        ...state,
        records: { ...state.records, error: action.payload, loading: false },
      };

    case FETCH_MAINTENANCE_RECORD.init:
      return {
        ...state,
        records: { ...state.records, error: null, loading: true },
      };
    case FETCH_MAINTENANCE_RECORD.success:
      return {
        ...state,
        records: {
          ...state.records,
          current: action.payload,
          error: null,
          loading: false,
        },
      };
    case FETCH_MAINTENANCE_RECORD.failure:
      return {
        ...state,
        records: { ...state.records, error: action.payload, loading: false },
      };

    case CREATE_MAINTENANCE_RECORD.init:
      return {
        ...state,
        records: { ...state.records, error: null, loading: true },
      };
    case CREATE_MAINTENANCE_RECORD.success:
      return {
        ...state,
        records: {
          ...state.records,
          list: [...state.records.list, action.payload],
          error: null,
          loading: false,
        },
      };
    case CREATE_MAINTENANCE_RECORD.failure:
      return {
        ...state,
        records: { ...state.records, error: action.payload, loading: false },
      };

    case UPDATE_MAINTENANCE_RECORD.init:
      return {
        ...state,
        records: { ...state.records, error: null, loading: true },
      };
    case UPDATE_MAINTENANCE_RECORD.success:
      return {
        ...state,
        records: {
          ...state.records,
          list: state.records.list.map((item) =>
            item.id === action.payload.id ? action.payload : item
          ),
          current: action.payload,
          error: null,
          loading: false,
        },
      };
    case UPDATE_MAINTENANCE_RECORD.failure:
      return {
        ...state,
        records: { ...state.records, error: action.payload, loading: false },
      };

    case DELETE_MAINTENANCE_RECORD.init:
      return {
        ...state,
        records: { ...state.records, error: null, loading: true },
      };
    case DELETE_MAINTENANCE_RECORD.success:
      return {
        ...state,
        records: {
          ...state.records,
          list: state.records.list.filter((item) => item.id !== action.payload),
          error: null,
          loading: false,
        },
      };
    case DELETE_MAINTENANCE_RECORD.failure:
      return {
        ...state,
        records: { ...state.records, error: action.payload, loading: false },
      };

    case START_MAINTENANCE_RECORD.init:
      return {
        ...state,
        records: { ...state.records, loading: true },
      };
    case START_MAINTENANCE_RECORD.success:
      const startedRecord = action.payload.data || action.payload;
      return {
        ...state,
        records: {
          ...state.records,
          list: state.records.list.map((item) =>
            item.id === startedRecord.id ? { ...item, ...startedRecord } : item
          ),
          current: state.records.current
            ? { ...state.records.current, ...startedRecord }
            : null,
          loading: false,
        },
      };
    case START_MAINTENANCE_RECORD.failure:
      return {
        ...state,
        records: { ...state.records, error: action.payload, loading: false },
      };

    case COMPLETE_MAINTENANCE_RECORD.init:
      return {
        ...state,
        records: { ...state.records, loading: true },
      };
    case COMPLETE_MAINTENANCE_RECORD.success:
      const completedRecord = action.payload.data || action.payload;
      return {
        ...state,
        records: {
          ...state.records,
          list: state.records.list.map((item) =>
            item.id === completedRecord.id ? { ...item, ...completedRecord } : item
          ),
          current: state.records.current
            ? { ...state.records.current, ...completedRecord }
            : null,
          loading: false,
        },
      };
    case COMPLETE_MAINTENANCE_RECORD.failure:
      return {
        ...state,
        records: { ...state.records, error: action.payload, loading: false },
      };

    case CANCEL_MAINTENANCE_RECORD.init:
      return {
        ...state,
        records: { ...state.records, loading: true },
      };
    case CANCEL_MAINTENANCE_RECORD.success:
      return {
        ...state,
        records: {
          ...state.records,
          list: state.records.list.map((item) =>
            item.id === action.payload.id
              ? { ...item, status: "CANCELLED" }
              : item
          ),
          loading: false,
        },
      };
    case CANCEL_MAINTENANCE_RECORD.failure:
      return {
        ...state,
        records: { ...state.records, error: action.payload, loading: false },
      };

    case RECORD_SERVICE_VISIT.init:
      return {
        ...state,
        records: { ...state.records, error: null, loading: true },
      };
    case RECORD_SERVICE_VISIT.success:
      const newRecord = action.payload.data || action.payload;
      return {
        ...state,
        records: {
          ...state.records,
          list: [...state.records.list, newRecord],
          error: null,
          loading: false,
        },
      };
    case RECORD_SERVICE_VISIT.failure:
      return {
        ...state,
        records: { ...state.records, error: action.payload, loading: false },
      };

    case CLEAR_CURRENT_RECORD:
      return {
        ...state,
        records: { ...state.records, current: null },
      };

    // =========================================================================
    // Photos
    // =========================================================================
    case FETCH_MAINTENANCE_PHOTOS.init:
      return {
        ...state,
        photos: { ...state.photos, hasMore: false, error: null, loading: true },
      };
    case FETCH_MAINTENANCE_PHOTOS.success:
      return {
        ...state,
        photos: {
          ...state.photos,
          list: action.payload.results || [],
          hasMore: action.payload.next !== null,
          pages: Math.ceil((action.payload.count || 0) / ITEMS_PER_PAGE),
          error: null,
          loading: false,
        },
      };
    case FETCH_MAINTENANCE_PHOTOS.failure:
      return {
        ...state,
        photos: { ...state.photos, error: action.payload, loading: false },
      };

    case UPLOAD_MAINTENANCE_PHOTO.init:
      return {
        ...state,
        photos: { ...state.photos, loading: true },
      };
    case UPLOAD_MAINTENANCE_PHOTO.success:
      const newPhoto = action.payload.data || action.payload;
      return {
        ...state,
        photos: {
          ...state.photos,
          list: [...state.photos.list, newPhoto],
          loading: false,
        },
      };
    case UPLOAD_MAINTENANCE_PHOTO.failure:
      return {
        ...state,
        photos: { ...state.photos, error: action.payload, loading: false },
      };

    case DELETE_MAINTENANCE_PHOTO.init:
      return {
        ...state,
        photos: { ...state.photos, loading: true },
      };
    case DELETE_MAINTENANCE_PHOTO.success:
      return {
        ...state,
        photos: {
          ...state.photos,
          list: state.photos.list.filter((item) => item.id !== action.payload),
          loading: false,
        },
      };
    case DELETE_MAINTENANCE_PHOTO.failure:
      return {
        ...state,
        photos: { ...state.photos, error: action.payload, loading: false },
      };

    // =========================================================================
    // Notifications
    // =========================================================================
    case FETCH_NOTIFICATIONS.init:
      return {
        ...state,
        notifications: { ...state.notifications, hasMore: false, error: null, loading: true },
      };
    case FETCH_NOTIFICATIONS.success:
      return {
        ...state,
        notifications: {
          ...state.notifications,
          list: action.payload.results || [],
          hasMore: action.payload.next !== null,
          pages: Math.ceil((action.payload.count || 0) / ITEMS_PER_PAGE),
          error: null,
          loading: false,
        },
      };
    case FETCH_NOTIFICATIONS.failure:
      return {
        ...state,
        notifications: { ...state.notifications, error: action.payload, loading: false },
      };

    case FETCH_UNREAD_COUNT.init:
      return state;
    case FETCH_UNREAD_COUNT.success:
      return {
        ...state,
        notifications: {
          ...state.notifications,
          unreadCount: action.payload.unread_count || 0,
        },
      };
    case FETCH_UNREAD_COUNT.failure:
      return state;

    case MARK_NOTIFICATION_READ.init:
      return state;
    case MARK_NOTIFICATION_READ.success:
      return {
        ...state,
        notifications: {
          ...state.notifications,
          list: state.notifications.list.map((item) =>
            item.id === action.payload.id ? { ...item, is_read: true } : item
          ),
          unreadCount: Math.max(0, state.notifications.unreadCount - 1),
        },
      };
    case MARK_NOTIFICATION_READ.failure:
      return state;

    case MARK_ALL_NOTIFICATIONS_READ.init:
      return state;
    case MARK_ALL_NOTIFICATIONS_READ.success:
      return {
        ...state,
        notifications: {
          ...state.notifications,
          list: state.notifications.list.map((item) => ({ ...item, is_read: true })),
          unreadCount: 0,
        },
      };
    case MARK_ALL_NOTIFICATIONS_READ.failure:
      return state;

    // =========================================================================
    // UI State
    // =========================================================================
    case SET_ACTIVE_TAB:
      return {
        ...state,
        ui: { ...state.ui, activeTab: action.payload },
      };

    case CLEAR_MAINTENANCE_ERROR:
      return {
        ...state,
        dashboard: { ...state.dashboard, error: null },
        machineTypes: { ...state.machineTypes, error: null },
        facilityTypes: { ...state.facilityTypes, error: null },
        locations: { ...state.locations, error: null },
        serviceProviders: { ...state.serviceProviders, error: null },
        machines: { ...state.machines, error: null },
        facilities: { ...state.facilities, error: null },
        taskTemplates: { ...state.taskTemplates, error: null },
        taskAssignments: { ...state.taskAssignments, error: null },
        scheduledTasks: { ...state.scheduledTasks, error: null },
        records: { ...state.records, error: null },
        photos: { ...state.photos, error: null },
        notifications: { ...state.notifications, error: null },
        ui: { ...state.ui, error: null },
      };

    default:
      return state;
  }
};

export default maintenanceReducer;
